import React, {useContext, useState} from 'react';
import {observer} from 'mobx-react-lite';
import {
    Button,
    Grid,
    GridColumn,
    Modal,
    ModalCloser,
    ModalContent,
    ModalFooter,
    ModalHeader,
    Spinner,
    TextField,
} from '../../../patterns';
import {StoreContext} from '../base/root-store';

export const ApiKeyModal = observer((): JSX.Element => {
    const store = useContext(StoreContext).companyStore;
    const existingApiKey = store.payload?.apiKey;
    const [generatedKey, setGeneratedKey] = useState<string | null>(null);
    const [copied, setCopied] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isRevoking, setIsRevoking] = useState(false);

    const handleGenerate = async (): Promise<void> => {
        setIsGenerating(true);
        try {
            const key = await store.generateApiKey();

            if (key) {
                setGeneratedKey(key);
            }
        } finally {
            setIsGenerating(false);
        }
    };

    const handleRevoke = async (): Promise<void> => {
        if (window.confirm(store.label('company.api-key.revoke-confirm'))) {
            setIsRevoking(true);
            try {
                await store.revokeApiKey();
            } finally {
                setIsRevoking(false);
            }
        }
    };

    const handleCopy = (): void => {
        if (generatedKey) {
            navigator.clipboard.writeText(generatedKey);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const formatDate = (dateString: string): string => {
        const date = new Date(dateString);

        return date.toLocaleDateString();
    };

    return (
        <Modal size="small">
            <ModalHeader>
                <h4>{store.label('company.api-key.title')}</h4>
            </ModalHeader>
            <ModalContent>
                <div className="text">
                    {generatedKey ? (
                        <div>
                            <p className="text--warning">{store.label('company.api-key.copy-warning')}</p>
                            <div className="input-wrapper">
                                <TextField
                                    label={store.label('company.api-key.generated-key')}
                                    value={generatedKey}
                                    id="api-key"
                                    name="api-key"
                                    attributes={{ readOnly: true }}
                                />
                            </div>
                            <Button
                                color="white"
                                text={copied ? store.label('company.api-key.copied') : store.label('company.api-key.copy')}
                                onClick={handleCopy}
                            />
                        </div>
                    ) : existingApiKey ? (
                        <div>
                            <div className="input-wrapper">
                                <TextField
                                    label={store.label('company.api-key.current-key')}
                                    value={existingApiKey.maskedKey}
                                    id="api-key-masked"
                                    name="api-key-masked"
                                    attributes={{ readOnly: true }}
                                />
                            </div>
                            <p className="text--muted">
                                {store.label('company.api-key.created-by', { name: existingApiKey.createdByName })}
                                {' '}
                                {store.label('company.api-key.created-at', { date: formatDate(existingApiKey.createdAt) })}
                            </p>
                        </div>
                    ) : (
                        <p>{store.label('company.api-key.no-key')}</p>
                    )}
                </div>
            </ModalContent>
            <ModalFooter>
                <Grid wrap={['xs']} justify={['between-xs']}>
                    <GridColumn width={['xs-12', 'sm-6']}>
                        <ModalCloser>
                            <Button
                                block={true}
                                color="white"
                                text={store.label('company.modal.close')}
                            />
                        </ModalCloser>
                    </GridColumn>
                    <GridColumn width={['xs-12', 'sm-6']}>
                        {generatedKey ? (
                            <ModalCloser>
                                <Button
                                    block={true}
                                    color="green"
                                    text={store.label('company.modal.done')}
                                />
                            </ModalCloser>
                        ) : existingApiKey ? (
                            <div className="button-with-spinner">
                                <Button
                                    block={true}
                                    color="red"
                                    disabled={isRevoking}
                                    text={store.label('company.api-key.revoke')}
                                    onClick={handleRevoke}
                                />
                                {isRevoking && <Spinner className="button-with-spinner__spinner" />}
                            </div>
                        ) : (
                            <div className="button-with-spinner">
                                <Button
                                    block={true}
                                    color="green"
                                    disabled={isGenerating}
                                    text={store.label('company.api-key.generate')}
                                    onClick={handleGenerate}
                                />
                                {isGenerating && <Spinner className="button-with-spinner__spinner" />}
                            </div>
                        )}
                    </GridColumn>
                </Grid>
            </ModalFooter>
        </Modal>
    );
});
